import { useState, useEffect, useCallback } from 'react';
import { careerService } from '../services/CareerService';
import useAlert from '../../../shared/hooks/useAlert';
import { usePagination } from '../../../shared/hooks/usePagination';
import Swal from 'sweetalert2';

export const useCareer = () => {
  const { alert, showAlert } = useAlert();
  const {
    pagination, data, meta, loading, error,
    setData, setLoading, setError, setMeta,
    goToPage, changeLimit, changeSort, toggleSortOrder,
    search, resetPagination, currentSortField, currentSortOrder,
    hasNextPage, hasPreviousPage, nextPage, previousPage,
  } = usePagination();

  const [filters, setFilters] = useState({
    employment_type: 'ALL',
    status: 'ALL',
  });

  const fetchCareers = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: pagination.sortBy,
        search: pagination.search,
        ...(filters.employment_type !== 'ALL' && { employment_type: filters.employment_type }),
        ...(filters.status !== 'ALL' && { status: filters.status }),
      }).toString();

      const result = await careerService.getPaginate(queryParams);
      if (result.success) {
        setData(result.data.data);
        setMeta(result.data.meta);
      } else {
        setError(result.error);
        showAlert(result.error, 'danger');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, pagination.sortBy, pagination.search, filters, setData, setMeta, setError, setLoading, showAlert]);

  // ================= CRUD OPERATIONS =================

  const addCareer = useCallback(async (formData) => {
    try {
      const result = await careerService.create(formData);
      if (result.success) {
        // Notifikasi Berhasil
        Swal.fire({
          icon: 'success',
          title: 'Berhasil!',
          text: 'Lowongan baru telah berhasil ditambahkan.',
          timer: 2000,
          showConfirmButton: false
        });

        fetchCareers(); // RELOAD TABLE
        return { success: true };
      } else {
        Swal.fire('Gagal!', result.error, 'error');
        return { success: false };
      }
    } catch (err) {
      Swal.fire('Error!', 'Terjadi kesalahan sistem', 'error');
      return { success: false };
    }
  }, [fetchCareers]);

  const editCareer = useCallback(async (id, formData) => {
    try {
      const result = await careerService.update(id, formData);
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil Diperbarui!',
          text: 'Perubahan data lowongan telah disimpan.',
          timer: 2000,
          showConfirmButton: false
        });

        fetchCareers(); // RELOAD TABLE
        return { success: true };
      } else {
        Swal.fire('Gagal!', result.error, 'error');
        return { success: false };
      }
    } catch (err) {
      Swal.fire('Error!', 'Gagal memperbarui data', 'error');
      return { success: false };
    }
  }, [fetchCareers]);

  const removeCareer = useCallback(async (id) => {
    // Gunakan Swal untuk konfirmasi delete agar UI konsisten
    const confirm = await Swal.fire({
      title: 'Apakah Anda yakin?',
      text: "Data yang dihapus tidak dapat dikembalikan!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, hapus!',
      cancelButtonText: 'Batal'
    });

    if (confirm.isConfirmed) {
      try {
        const result = await careerService.delete(id);
        if (result.success) {
          Swal.fire('Terhapus!', 'Lowongan telah dihapus.', 'success');
          fetchCareers(); // RELOAD TABLE
        } else {
          Swal.fire('Gagal!', result.error, 'error');
        }
      } catch (err) {
        Swal.fire('Error!', 'Gagal menghapus data', 'error');
      }
    }
  }, [fetchCareers]);

  // ================= FILTER ACTIONS =================

  const updateEmploymentTypeFilter = (value) => { setFilters(p => ({ ...p, employment_type: value })); goToPage(1); };
  const updateStatusFilter = (value) => { setFilters(p => ({ ...p, status: value })); goToPage(1); };
  const clearFilters = () => { setFilters({ employment_type: 'ALL', status: 'ALL' }); goToPage(1); };

  useEffect(() => {
    fetchCareers();
  }, [pagination.page, pagination.limit, pagination.sortBy, pagination.search, filters.employment_type, filters.status]);

  return {
    careers: data, loading, error, alert,
    pagination, meta, filters,
    addCareer, editCareer, removeCareer, // Sekarang CRUD ada di sini
    updateEmploymentTypeFilter, updateStatusFilter, clearFilters,
    goToPage, changeLimit, changeSort, toggleSortOrder, search,
    hasNextPage, hasPreviousPage, nextPage, previousPage,
    currentSortField, currentSortOrder, fetchCareers
  };
};